/**
 * The hypostyle hall: an Egyptian temple room where the roof is carried by a
 * forest of papyrus columns rather than by the walls. The visitor never sees
 * the whole space at once — the columns slice it into shifting views, and the
 * only light is the pair of clerestory slots that run high along the long
 * walls, the way a real hypostyle is lit through the raised central nave.
 *
 * The aisles come first and the grid is laid out around them: every door axis
 * is a 2.8m corridor, the column lines fall in the bays either side of it, and
 * any column left standing on the line between two facing doors is dropped.
 */

import { rectSegments } from '../hang';

const COL_R = 0.55; // shaft radius
const COL_SEG = 12; // radial segments per shaft (cheap, still reads round)
const COL_MARGIN = 2.0; // grid inset from the interior wall face
const COL_SPACING = 3.6; // minimum centre-to-centre spacing
const AISLE_HALF = 1.4; // half of the 2.8m aisle kept on every door axis
const CROSS_HALF = 1.0; // half-width kept clear on door-to-door diagonals
const STRIP_HALF = 0.45; // half-width of a clerestory slot
const STRIP_OFF = 0.85; // slot centre, in from the long wall face
const STRIP_KERB = 0.11; // reveal round a slot; slot + kerb must clear the columns
const OPPOSITE = { N: 'S', S: 'N', E: 'W', W: 'E' };

// Evenly spaced grid line positions across [a1,a2], never closer than `min`.
const gridLine = (a1, a2, min) => {
  const span = a2 - a1;
  if (span < min) return [(a1 + a2) / 2];
  const n = 1 + Math.floor(span / min); // n points, n-1 gaps, each >= min
  const step = span / (n - 1);
  const out = [];
  for (let i = 0; i < n; i++) out.push(a1 + i * step);
  return out;
};

// The same, but with the aisles removed from the range first, so the grid
// lines land in the bays *between* the aisles instead of being culled out of
// them afterwards — which is how a real hypostyle flanks its cross-axes, and
// keeps a four-door hall from ending up with two lonely columns.
const linesAvoiding = (a1, a2, blocked, min) => {
  const spans = [];
  let cursor = a1;
  for (const [lo, hi] of blocked.slice().sort((p, q) => p[0] - q[0])) {
    if (hi <= cursor) continue;
    if (lo > cursor) spans.push([cursor, Math.min(lo, a2)]);
    cursor = Math.max(cursor, hi);
    if (cursor >= a2) break;
  }
  if (cursor < a2) spans.push([cursor, a2]);

  const out = [];
  for (const [s1, s2] of spans) {
    if (s2 - s1 < 0.05) continue;
    out.push(...gridLine(s1, s2, min));
  }
  return out;
};

// Where each door meets the footprint edge, in world space.
const doorPoints = (node) =>
  (node.doors || []).map((d) => ({
    x: d.side === 'E' ? node.x2 : d.side === 'W' ? node.x1 : d.at,
    z: d.side === 'N' ? node.z2 : d.side === 'S' ? node.z1 : d.at,
    side: d.side,
    at: d.at,
  }));

const distToSeg = (px, pz, ax, az, bx, bz) => {
  const vx = bx - ax;
  const vz = bz - az;
  const l2 = vx * vx + vz * vz;
  const t = l2 > 0 ? Math.max(0, Math.min(1, ((px - ax) * vx + (pz - az) * vz) / l2)) : 0;
  return Math.hypot(px - (ax + t * vx), pz - (az + t * vz));
};

const hypostyle = {
  id: 'hypostyle',
  role: 'wing',
  weight: 1.5,

  plan(rand, { axis }) {
    const long = 14 + rand() * 3;
    const cross = 12 + rand() * 2;
    return {
      w: axis === 'x' ? long : cross,
      d: axis === 'x' ? cross : long,
      height: 10,
    };
  },

  segments(node) {
    return rectSegments(node, { longStyle: 'cluster', shortStyle: 'cluster' });
  },

  build(node, T) {
    const { WALL_T } = T.PALACE;
    const height = node.height;
    const { group, colliders } = T.standardShell(node, T.kit, {
      floorMaterial: T.kit.materials.marble,
      laylight: false, // clerestory slots instead of a central coffer
      corniceH: 0.5,
    });

    const xI1 = node.x1 + WALL_T;
    const xI2 = node.x2 - WALL_T;
    const zI1 = node.z1 + WALL_T;
    const zI2 = node.z2 - WALL_T;
    const cx = (xI1 + xI2) / 2;
    const cz = (zI1 + zI2) / 2;
    const longX = xI2 - xI1 >= zI2 - zI1;

    // --- clerestory: two slots hugging the long walls, just under the ceiling
    const trim = [];
    const stripLen = Math.max(2, (longX ? xI2 - xI1 : zI2 - zI1) - 3);
    const border = (bx1, bx2, bz1, bz2, y0, y1, t) => {
      trim.push(
        T.box(bx1 - t, bx2 + t, y0, y1, bz1 - t, bz1),
        T.box(bx1 - t, bx2 + t, y0, y1, bz2, bz2 + t),
        T.box(bx1 - t, bx1, y0, y1, bz1, bz2),
        T.box(bx2, bx2 + t, y0, y1, bz1, bz2)
      );
    };
    for (const s of [-1, 1]) {
      const off = longX
        ? (s < 0 ? zI1 + STRIP_OFF : zI2 - STRIP_OFF)
        : (s < 0 ? xI1 + STRIP_OFF : xI2 - STRIP_OFF);
      const sx = longX ? cx : off;
      const sz = longX ? off : cz;
      const halfX = longX ? stripLen / 2 : STRIP_HALF;
      const halfZ = longX ? STRIP_HALF : stripLen / 2;

      const plane = new T.THREE.Mesh(T.kit.geometries.unitPlane, T.kit.materials.laylight);
      plane.rotation.x = Math.PI / 2;
      plane.scale.set(halfX * 2, halfZ * 2, 1);
      plane.position.set(sx, height - 0.03, sz);
      plane.matrixAutoUpdate = false;
      plane.updateMatrix();
      group.add(plane);

      border(sx - halfX, sx + halfX, sz - halfZ, sz + halfZ, height - 0.22, height, STRIP_KERB);
    }
    group.add(T.mergedMesh(trim, T.kit.materials.trim));

    // --- the column grid. Every door's axis is a 2.8m aisle: the grid lines
    // are laid out in the bays either side of it, so no column ever stands in
    // one. Doors that face each other also get their diagonal cleared.
    const doors = doorPoints(node);
    const aisle = AISLE_HALF + COL_R + 0.1;
    const bands = (horizontal) =>
      doors
        .filter((d) => (d.side === 'N' || d.side === 'S') === horizontal)
        .map((d) => [d.at - aisle, d.at + aisle]);

    const clearOfDiagonals = (px, pz) => {
      for (let i = 0; i < doors.length; i++) {
        for (let j = i + 1; j < doors.length; j++) {
          const a = doors[i];
          const b = doors[j];
          if (OPPOSITE[a.side] !== b.side) continue;
          if (distToSeg(px, pz, a.x, a.z, b.x, b.z) < CROSS_HALF + COL_R) return false;
        }
      }
      return true;
    };

    const stone = [];
    const shaftTop = height - 1.15;
    const xs = linesAvoiding(xI1 + COL_MARGIN, xI2 - COL_MARGIN, bands(true), COL_SPACING);
    const zs = linesAvoiding(zI1 + COL_MARGIN, zI2 - COL_MARGIN, bands(false), COL_SPACING);
    for (const px of xs) {
      for (const pz of zs) {
        if (!clearOfDiagonals(px, pz)) continue;

        // Slightly tapered shaft, flared papyrus-bell capital, square abacus,
        // impost block carrying the ceiling.
        const shaft = new T.THREE.CylinderGeometry(COL_R * 0.92, COL_R, shaftTop, COL_SEG, 1, false);
        shaft.translate(px, shaftTop / 2, pz);
        const bell = new T.THREE.CylinderGeometry(
          COL_R * 1.35, COL_R * 0.92, 0.55, COL_SEG, 1, false
        );
        bell.translate(px, shaftTop + 0.275, pz);
        stone.push(
          shaft,
          bell,
          T.box(px - 0.78, px + 0.78, shaftTop + 0.55, shaftTop + 0.9, pz - 0.78, pz + 0.78),
          T.box(px - 0.55, px + 0.55, shaftTop + 0.9, height + 0.05, pz - 0.55, pz + 0.55)
        );
        colliders.push({ circle: [px, pz, COL_R + 0.06], top: height });
      }
    }
    if (stone.length) group.add(T.mergedMesh(stone, T.kit.materials.stone));

    return { group, colliders };
  },
};

export default hypostyle;
